import { z } from 'zod';
import { RUNTIME_V3_PERIOD_GRAINS } from './common';
import { isRuntimeV3ContentDigest } from './canonical-json';
import type { ObjectiveCutDescriptor, ObjectiveIdentity, ObjectiveSource, ObjectiveSourceContract } from './objective-contract';
import type { RuntimeV3DefinitionVersionRef } from './definitions';

const text = z.string().trim().min(1).max(2048);
const digest = z.custom<string>(isRuntimeV3ContentDigest);
const day = z.string().date();
export const objectiveIdentitySchema = z.object({ tenantId: text, objectiveBindingId: text,
  goalVersionRef: text, strategyId: text, scopeRef: text }).strict();
export const objectivePeriodSchema = z.object({ start: day, end: day,
  grain: z.enum(RUNTIME_V3_PERIOD_GRAINS), calendar: text }).strict().refine(value => value.start <= value.end);
export const objectiveDefinitionPinSchema = z.object({ definitionRef: text,
  scopeKind: z.enum(['tenant', 'shared']), tenantId: text.nullable(), digest: digest.optional(),
  subset: z.object({ collection: text, keys: z.array(text).min(1), digest }).strict().optional(),
}).strict().refine(value => (value.scopeKind === 'shared') === (value.tenantId === null)
  && (value.digest === undefined) !== (value.subset === undefined));
const pin = z.object({ tenantId: text, datasetRef: text, revisionId: text,
  version: z.number().int().positive(), digest, rowContentDigest: digest.optional() }).strict();
const fixedScale = z.object({ value: text, rawValue: text, scale: z.number().int().nonnegative() }).strict();
const promise = z.object({ label: text, metricKey: text, unit: text, direction: text,
  baselineValue: fixedScale, targetValue: fixedScale, timeframeStart: z.string().datetime({ offset: true }),
  timeframeEnd: z.string().datetime({ offset: true }), reviewCadence: text,
  ownerUserId: z.object({ userRef: z.object({ refKind: z.literal('user'), id: text,
    tenantRef: text.optional() }).strict().nullable(), displayName: text,
    resolution: z.enum(['resolved', 'not_recorded']) }).strict(),
  approvedAt: z.string().datetime({ offset: true }) }).strict();
const fields = {
  promise: z.object({ objective: text, metric: text, unit: text, direction: text,
    baseline: text, target: text, periodStart: text, periodEnd: text, owner: text, cadence: text }).strict(),
  actuals: z.object({ recordId: text, objective: text, population: text, period: text,
    asOf: text, value: text, completeness: text }).strict(),
  forecast: z.object({ objective: text, metric: text, horizon: text, scenario: text,
    band: text, assumptions: text, engineInputs: z.array(text).min(1) }).strict(),
  dependencies: z.object({ taskId: text, waitsOn: text, plannedAt: text, forecastAt: text,
    actualAt: text, releaseRecord: text, conditions: text, evidenceRow: text }).strict(),
  people: z.object({ personId: text, seatId: text, effectiveFrom: text, effectiveTo: text,
    accountableScope: text, action: text, permissionClass: text, displayName: text.optional() }).strict(),
  options: z.object({ optionId: text, population: text, effects: z.array(text).min(1), timing: text,
    capacityConstraints: text, serviceConstraints: text, overlap: text, exclusivity: text,
    recurringCost: text, oneTimeCost: text, approval: text }).strict(),
  outcomes: z.object({ investigationRevision: text, optionRevision: text, actionId: text,
    owner: text, dueAt: text, checkAt: text, approvedExpectation: text,
    observationSource: text, outcomeRecord: text, actionText: text.optional(), catch: text.optional(),
    firstCheckText: text.optional(), actual: text.optional() }).strict(),
};
const source = z.object({ slot: text, pin, definition: objectiveDefinitionPinSchema,
  recordKeys: z.array(z.record(text).refine(value => Object.keys(value).length > 0)).min(1).max(1000),
  selection: z.object({ field: text, dimensionRef: text, memberRefs: z.array(text).min(1).max(1000) }).strict().optional(),
  period: objectivePeriodSchema, unit: text,
  calculation: z.object({ kind: z.enum(['published_measure', 'engine']), ref: text,
    definition: objectiveDefinitionPinSchema }).strict() });
export const objectiveSourceSchema: z.ZodType<ObjectiveSource> = z.discriminatedUnion('role', [
  source.extend({ role: z.literal('promise'), fields: fields.promise }).strict(),
  source.extend({ role: z.literal('actuals'), fields: fields.actuals }).strict(),
  source.extend({ role: z.literal('forecast'), fields: fields.forecast }).strict(),
  source.extend({ role: z.literal('dependencies'), fields: fields.dependencies }).strict(),
  source.extend({ role: z.literal('people'), fields: fields.people }).strict(),
  source.extend({ role: z.literal('options'), fields: fields.options }).strict(),
  source.extend({ role: z.literal('outcomes'), fields: fields.outcomes }).strict(),
]);
export const objectiveSourceContractSchema: z.ZodType<ObjectiveSourceContract> = z.object({ schemaVersion: z.literal('objective-source.v1'),
  identity: objectiveIdentitySchema, promise, period: objectivePeriodSchema,
  sources: z.array(objectiveSourceSchema).min(1).max(100) }).strict();
export const objectiveCutDescriptorSchema: z.ZodType<ObjectiveCutDescriptor> = z.object({ kind: z.literal('objective'), id: digest,
  identity: objectiveIdentitySchema, asOf: day, period: objectivePeriodSchema,
  unit: text, contractPin: objectiveDefinitionPinSchema.optional(),
  sources: z.array(objectiveSourceSchema).min(1).max(100) }).strict().refine(value => value.sources.every(entry =>
    entry.pin.tenantId === value.identity.tenantId
    && (entry.definition.scopeKind === 'shared' || entry.definition.tenantId === value.identity.tenantId)
    && (entry.calculation.definition.scopeKind === 'shared'
      || entry.calculation.definition.tenantId === value.identity.tenantId)));

/** Page selection names authority; it carries neither promise values nor guessed source roles. */
export type ObjectivePageContext = Readonly<{ kind: 'objective'; identity: ObjectiveIdentity;
  contractPin: RuntimeV3DefinitionVersionRef; asOf: string }>;
export const objectivePageContextSchema: z.ZodType<ObjectivePageContext> = z.object({ kind: z.literal('objective'),
  identity: objectiveIdentitySchema, contractPin: objectiveDefinitionPinSchema, asOf: day }).strict();

export const objectiveSubjectSchema = z.object({ kind: z.literal('objective'), ref: text,
  tenantId: text, strategyId: text, scopeRef: text }).strict();
export const objectiveSavedReadAddressSchema = z.object({ kind: z.literal('objective'), goalVersionRef: text,
  asOf: day, viewId: z.enum(['plan', 'behind', 'act', 'result']), params: z.object({}).strict(), cutId: digest }).strict();
export const objectiveViewAddressSchema = objectiveSavedReadAddressSchema.omit({ cutId: true }).extend({
  figureCut: objectiveCutDescriptorSchema }).strict().refine(value =>
  value.goalVersionRef === value.figureCut.identity.goalVersionRef && value.asOf === value.figureCut.asOf);
export function objectiveSubjectMatchesAddress(subject: z.infer<typeof objectiveSubjectSchema>,
  address: z.infer<typeof objectiveViewAddressSchema>): boolean {
  const identity = address.figureCut.identity;
  return subject.ref === identity.goalVersionRef && subject.tenantId === identity.tenantId
    && subject.strategyId === identity.strategyId && subject.scopeRef === identity.scopeRef;
}
/** Shared request grammar; persistence completion states remain PR2's concern. */
export const objectiveInvestigationRequestSchema = z.object({ kind: z.literal('five_why'),
  subject: objectiveSubjectSchema, address: objectiveViewAddressSchema,
  expectedParentArtifactId: text.nullable() }).strict().refine(value =>
  objectiveSubjectMatchesAddress(value.subject, value.address));
export const objectiveInvestigationReadRequestShape = z.object({
  subject: objectiveSubjectSchema.pick({ kind: true, ref: true }),
  address: z.union([objectiveViewAddressSchema, objectiveSavedReadAddressSchema]),
}).strict();
export const objectiveInvestigationReadRequestSchema = objectiveInvestigationReadRequestShape
  .refine(value => value.subject.ref === value.address.goalVersionRef);
